import React from 'react'
import {
  ArrowLeft,
  Upload,
  Edit3,
  ImageIcon,
  Check,
  Loader2,
} from 'lucide-react'
import { cn } from '@/utils/cn'
import { ClassItem, ImageItem } from '@/types/catalog'
import { Category } from '@/types'
import { formatClassNameForDisplay } from '@/utils/classNameUtils'
import AuthenticatedImage from '@/components/AuthenticatedImage'

interface MobileCatalogImageGridProps {
  classData: ClassItem
  images: ImageItem[]
  isAdmin: boolean
  isUploading: boolean
  uploadProgress: { current: number; total: number }
  thumbnailFileName: string | null | undefined
  category: Category | null
  onBack: () => void
  onImageTap: (imageId: string) => void
  onUpload: () => void
  onEditClass: () => void
  onSelectThumbnail: () => void
}

const MobileCatalogImageGrid: React.FC<MobileCatalogImageGridProps> = ({
  classData,
  images,
  isAdmin,
  isUploading,
  uploadProgress,
  thumbnailFileName,
  category,
  onBack,
  onImageTap,
  onUpload,
  onEditClass,
  onSelectThumbnail,
}) => {
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3 safe-area-top">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 -ml-2 text-gray-600 active:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold text-gray-900 truncate">
                {formatClassNameForDisplay(classData.displayName)}
              </h1>
              {isAdmin && (
                <button
                  onClick={onEditClass}
                  className="p-1 text-gray-400 active:text-gray-600"
                >
                  <Edit3 className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 text-sm text-gray-500">
              {category && (
                <span
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
                  style={{
                    backgroundColor: `${category.color}20`,
                    color: category.color,
                  }}
                >
                  {category.name}
                </span>
              )}
              <span>{images.length} images</span>
            </div>
          </div>

          {/* Upload button */}
          {isAdmin && (
            <button
              onClick={onUpload}
              disabled={isUploading}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
                isUploading
                  ? 'bg-gray-100 text-gray-400'
                  : 'bg-primary-600 text-white active:bg-primary-700'
              )}
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">Upload</span>
            </button>
          )}
        </div>

        {/* Upload progress bar */}
        {isUploading && uploadProgress.total > 0 && (
          <div className="mt-2">
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-primary-600 h-1.5 rounded-full transition-all duration-300"
                style={{
                  width: `${(uploadProgress.current / uploadProgress.total) * 100}%`
                }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Uploading {uploadProgress.current} of {uploadProgress.total}
            </p>
          </div>
        )}
      </div>

      {/* Image Grid */}
      <div className="flex-1 overflow-y-auto pb-20">
        {images.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <ImageIcon className="h-12 w-12 text-gray-300 mb-3" />
            <p className="text-lg font-medium text-gray-700">No images yet</p>
            <p className="text-sm mb-4">Upload images to this class</p>
            {isAdmin && (
              <button
                onClick={onUpload}
                className="flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-xl font-medium active:bg-primary-700 transition-colors"
              >
                <Upload className="h-4 w-4" />
                <span>Upload Images</span>
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 p-3">
            {images.map((image) => {
              const isThumbnail = thumbnailFileName === image.fileName

              return (
                <button
                  key={image.id}
                  onClick={() => onImageTap(image.id)}
                  className={cn(
                    'relative aspect-square rounded-xl overflow-hidden bg-gray-100',
                    'active:opacity-80 transition-opacity',
                    isThumbnail && 'ring-2 ring-primary-500 ring-offset-2'
                  )}
                >
                  <AuthenticatedImage
                    imageUrl={image.imageUrl}
                    alt={image.fileName}
                    className="w-full h-full object-cover"
                    size="small"
                  />

                  {/* Thumbnail indicator */}
                  {isThumbnail && (
                    <div className="absolute top-2 left-2 bg-primary-500 text-white rounded-full p-1">
                      <Check className="h-3 w-3" />
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default MobileCatalogImageGrid
